import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Insignia, Tarjeta } from './ui/basicos';
import { usePermiso } from '../sesion/ProveedorDeSesion';

interface Espera {
  readonly tipo: string;
  readonly destino: string;
  readonly destinoId: string;
  readonly cuantos: number;
}

/**
 * Lo que quedó esperando su documento posterior.
 *
 * ## Por qué cuenta en vez de listar
 *
 * Lo que hay que saber al entrar es **cuántos faltan**; el detalle está en Facturar entregas, a un
 * clic. Una lista de cuarenta remitos en la pantalla de inicio empuja hacia abajo todo lo demás y
 * no se lee.
 *
 * ## Por qué no aparece cuando no hay nada
 *
 * Igual que los pendientes de seguimiento: una tarjeta vacía que dice «no falta nada» ocupa el
 * mismo lugar todos los días y enseña a no mirar ahí.
 */
export function LoQueEspera(): React.ReactElement | null {
  const { t } = useTranslation();
  const puedeVer = usePermiso('VENTAS.COMPROBANTE.EMITIR');

  const espera = useQuery({
    queryKey: ['lo-que-espera'],
    enabled: puedeVer,
    queryFn: () => pedir<Espera[]>('/conversiones/lo-que-espera'),
  });

  const filas = espera.data ?? [];
  if (filas.length === 0) return null;

  return (
    <Tarjeta>
      <h2 className="mb-2 text-seccion font-semibold">{t('circuito.titulo')}</h2>
      <ul className="flex flex-col divide-y divide-borde-tenue">
        {filas.map((fila) => (
          <li key={fila.destinoId + fila.tipo} className="flex items-center gap-3 py-2 text-cuerpo">
            <Insignia tono="atencion">{fila.cuantos}</Insignia>
            <span className="min-w-0 flex-1">
              {t('circuito.esperan', { tipo: fila.tipo, destino: fila.destino })}
            </span>
            {/* Al lugar donde se resuelve, no a una pantalla de detalle: quien mira esto quiere
                terminar el trabajo, no leer sobre él. */}
            <Link to="/conversiones" className="text-acento underline">
              {t('circuito.resolver')}
            </Link>
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
}
